import { useEffect, useState, useMemo } from "react";
import {
  getBatchesForAuthorization,
  getCopywritersForAuthorization,
  getBrandsForAuthorization,
} from "../../api/authorization";
import { AuthorizationBatch } from "./authorization-types";
import { User, Brand } from "../../pages/playground/types";
import { JobAuthorizationBatchList } from "./job-authorization-batch-list";
import {
  CircularProgress,
  Box,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import JobAuthorizationBatch from "./job-authorization-batch";

export default function JobAuthorization() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [batches, setBatches] = useState<AuthorizationBatch[]>([]);
  const [copywriters, setCopywriters] = useState<User[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  // Filter brands based on search query
  const filteredBrands = useMemo(() => {
    if (!brandSearchQuery) return brands;
    const query = brandSearchQuery.toLowerCase();
    return brands.filter((brand) =>
      brand.name.toLowerCase().includes(query)
    );
  }, [brands, brandSearchQuery]);

  const fetchInitialData = async () => {
    try {
      setIsInitialLoading(true);

      const copywritersResponse = await getCopywritersForAuthorization();
      const brandsResponse = await getBrandsForAuthorization();

      setCopywriters(copywritersResponse.data.data);
      setBrands(brandsResponse.data.data);

      // Now fetch batches
      await fetchBatches();
    } finally {
      setIsInitialLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      setIsLoadingBatches(true);
      setPage(1);

      const brandIdsParam = selectedBrandIds.length > 0 ? selectedBrandIds : undefined;
      const batchesResponse = await getBatchesForAuthorization(1, 20, brandIdsParam);

      setBatches(batchesResponse.data.data.batches);
      setHasMore(batchesResponse.data.data.has_more);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const fetchMoreBatches = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const brandIdsParam = selectedBrandIds.length > 0 ? selectedBrandIds : undefined;
      const batchesResponse = await getBatchesForAuthorization(nextPage, 20, brandIdsParam);

      setBatches(prev => [...prev, ...batchesResponse.data.data.batches]);
      setHasMore(batchesResponse.data.data.has_more);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleBrandToggle = (brandId: string | undefined) => {
    if (!brandId) return;

    setSelectedBrandIds((prev) => {
      if (prev.includes(brandId)) {
        return prev.filter((id) => id !== brandId);
      } else {
        return [...prev, brandId];
      }
    });
  };

  const handleSelectAllBrands = () => {
    setSelectedBrandIds(brands.map((brand) => brand.id).filter((id): id is string => id !== undefined));
  };

  const handleDeselectAllBrands = () => {
    setSelectedBrandIds([]);
  };

  useEffect(() => {
    if (!isInitialLoading) {
      fetchBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandIds]);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isInitialLoading && <CircularProgress />}

      {!isInitialLoading && (
        <>
          {!selectedBatchId && (
            <>
              {/* Brand Filter Section */}
              <Paper elevation={1} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Filter by Brand
                    </Typography>
                    {selectedBrandIds.length > 0 && (
                      <Chip
                        label={`${selectedBrandIds.length} selected`}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                  <IconButton size="small">
                    {filterOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={filterOpen}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search brands..."
                        value={brandSearchQuery}
                        onChange={(e) => setBrandSearchQuery(e.target.value)}
                        sx={{ flexGrow: 1, mr: 1 }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAllBrands();
                          }}
                          sx={{ fontSize: "0.75rem", py: 0.5, whiteSpace: "nowrap" }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeselectAllBrands();
                          }}
                          sx={{ fontSize: "0.75rem", py: 0.5, whiteSpace: "nowrap" }}
                        >
                          Clear
                        </Button>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        maxHeight: 300,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      {filteredBrands.length === 0 ? (
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", textAlign: "center", py: 1 }}>
                          No brands found
                        </Typography>
                      ) : (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr", lg: "1fr 1fr 1fr 1fr" }, gap: 0 }}>
                          {filteredBrands.map((brand) => (
                            brand.id && (
                              <FormControlLabel
                                key={brand.id}
                                control={
                                  <Checkbox
                                    checked={selectedBrandIds.includes(brand.id)}
                                    onChange={() => handleBrandToggle(brand.id)}
                                    size="small"
                                  />
                                }
                                label={brand.name}
                                sx={{
                                  width: "100%",
                                  m: 0,
                                  py: 0.25,
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            )
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </Paper>

              {isLoadingBatches && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!isLoadingBatches && (
                <JobAuthorizationBatchList
                  batches={batches}
                  onSelectBatch={(batchId) => setSelectedBatchId(batchId)}
                  onLoadMore={fetchMoreBatches}
                  hasMore={hasMore}
                  loadingMore={loadingMore}
                />
              )}
            </>
          )}

          {selectedBatchId && (
            <JobAuthorizationBatch
              onClose={() => setSelectedBatchId(null)}
              batch={batches.find((batch) => batch.id === selectedBatchId)!}
              copywriters={copywriters}
            />
          )}
        </>
      )}
    </>
  );
}
